import {
    Attachment,
    Button,
    ButtonGroup,
    bytesToSize,
    classNames,
    dispatchCustomEvent,
    FDate,
    Footer,
    Heading,
    Icon,
    IconLib,
    Image,
    Popover,
    Portal,
    ScrollView,
    stopEvent,
    Text,
    UploadArea,
    useDialog,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
    TbArrowUp,
    TbArrowUpRight,
    TbCircleCheck,
    TbFileUpload,
    TbMessage,
    TbMessage2,
    TbMessageCircle,
    TbMessages,
    TbMoodSmile,
    TbPencil,
    TbRocket,
    TbSend,
    TbTrash,
} from 'react-icons/tb'
import { v4 as uuidv4 } from 'uuid'
import {
    aggregateReactions,
    extractUrlFromHtml,
    getMentionsfromTipTapJSON,
    isRichTextBlank,
    REACTIONS,
    stripS3Url,
} from '../../helpers'
import { useTaskCommentAttachments, useTaskCommentReactions, useTaskComments, useTools } from '../../hooks/api.hooks'
import { useAuth } from '../../hooks/auth.hook'
import { useFile } from '../../hooks/file.hook'
import { useAppStore } from '../../store/app'
import { LoadingLine } from '../common'
import { PresenceAvatar } from '../common/presence-avatar'
import { Tiptap } from '../tiptap'
import { CommentAttachment, CONSECUTIVE_TIME, InputAttachment } from '../messages'
import EmojiPicker, { Emoji, EmojiClickData } from 'emoji-picker-react'

const CommentBubble = (props) => {
    const { getUserId } = useAuth()
    const { projectId, workspaceId, taskId, isBelow } = props
    const { show, hide, visible } = useVisibility()
    const { id, userId, userName, userImage, comment, attachments, createdAt, externalEmail } = props.comment
    const taskCommentReactionsAPI = useTaskCommentReactions()
    const taskCommentReactions = useMemo(() => props.comment?.reactions || [], [props.comment])
    const reactions: any = useMemo(() => aggregateReactions(taskCommentReactions), [taskCommentReactions])
    const isMe = useMemo(() => userId == getUserId(), [userId])
    const [counter, setCounter] = useState(0)
    const taskCommentAttachmentAPI = useTaskCommentAttachments()
    const dateText = useMemo(() => FDate(new Date(createdAt)).fromNow(), [createdAt, counter])
    const [edit, setEdit] = useState(false)
    const { og } = useTools()
    const [preview, setPreview] = useState<any>(null)
    const actions = useAppStore((state) => state.actions)
    const ref = useRef(null)
    const className = classNames({
        'f-message-bubble': true,
        'f-row': true,
        'reveal-on-hover': true,
        'is-edit': edit,
        'is-consecutive': props.isConsecutive,
    })

    const handleAttachmentDelete = async (attachment) => {
        actions.commentAttachmentRemove({ id: attachment.id })
        taskCommentAttachmentAPI.remove(workspaceId, projectId, taskId, id, attachment.id)
    }

    const handleReactionAdd = async (emoji: EmojiClickData) => {
        const reaction = emoji.unified
        const reactionId = uuidv4()
        const userId = getUserId()
        const reactions = [...taskCommentReactions, { id: reactionId, reaction, userId }]

        if (taskCommentReactions.findIndex((r) => r.reaction == reaction && r.userId == userId) != -1) return

        actions.commentUpdate({ id, reactions })
        hide()
        await taskCommentReactionsAPI.create(id, { id: reactionId, reaction })
    }

    const handleReactionDelete = async (reactions) => {
        const userReaction = reactions.find((r) => r.userId == getUserId())

        if (userReaction) {
            if (userReaction.userId != getUserId()) return
            const reactions = taskCommentReactions.filter((reaction) => reaction.id != userReaction.id)
            actions.commentUpdate({ id, reactions })
            await taskCommentReactionsAPI.remove(id, userReaction.id)
        } else {
            handleReactionAdd(userReaction.reaction)
        }
    }

    const fetchOpenGraph = async () => {
        const url = extractUrlFromHtml(comment)
        if (url) {
            try {
                const r = await og({ url })
                if (!r.error) {
                    if (!!r.data && !!r.data.result) setPreview({ ...r.data.result, url })
                    if (!!r.result) setPreview({ ...r.result, url })
                } else {
                    setPreview(null)
                }
            } catch (e) {
                setPreview(null)
                console.error(e)
            }
        } else {
            setPreview(null)
        }
    }

    useEffect(() => {
        fetchOpenGraph()
        const interval = setInterval(() => setCounter(new Date().getTime()), 60000)
        return () => clearInterval(interval)
    }, [comment])

    return (
        <div className={className}>
            <div className="f-message-bubble__avatar">
                <PresenceAvatar
                    userId={userId}
                    src={userImage}
                    name={userName || externalEmail} // this is the only case where it would be null
                    size="sm"
                    style={{
                        // 'outline': isMe ? '2px solid var(--f-color-accent)' : undefined,
                        // 'outlineOffset': 1,
                        '--f-avatar-background-color': 'var(--f-color-surface-stronger)',
                    }}
                />
            </div>

            <div className="f-message-bubble__content f-col">
                {!edit && (
                    <div
                        className="f-message-bubble__bubble f-text"
                        dangerouslySetInnerHTML={{ __html: comment }}
                    />
                )}

                <div className="f-message-bubble__toolbar f-row">
                    <ButtonGroup className={edit || visible ? undefined : 'reveal-on-hover-target'}>
                        {isMe && !edit && (
                            <Button
                                size="xs"
                                onClick={props.onDelete}>
                                <Icon
                                    icon={TbTrash}
                                    size="sm"
                                />
                            </Button>
                        )}

                        {isMe && !edit && (
                            <Button
                                size="xs"
                                onClick={() => {
                                    setEdit(true)
                                    props.onEditStart()
                                    setTimeout(() => ref.current.firstChild?.focus())
                                }}>
                                <Icon
                                    icon={TbPencil}
                                    size="sm"
                                />
                            </Button>
                        )}

                        {!edit && (
                            <Popover
                                width="fit-content"
                                portal={Portal}
                                content={
                                    <div className="f-message-bubble__reactions f-row">
                                        <EmojiPicker onEmojiClick={handleReactionAdd} />
                                    </div>
                                }
                                isVisible={visible}
                                onDismiss={hide}>
                                <Button
                                    size="xs"
                                    onClick={show}>
                                    <Icon
                                        icon={TbMoodSmile}
                                        size="sm"
                                    />
                                </Button>
                            </Popover>
                        )}
                    </ButtonGroup>
                </div>

                {edit && (
                    <div className="f-message-bubble__bubble-edit">
                        <Tiptap
                            ref={ref}
                            hideToolbar
                            content={comment}
                            onSubmit={props.onEdit}
                            workspaceId={props.workspaceId}
                            projectId={props.projectId}
                            teamId={props.teamId}
                            instanceId={id}
                        />

                        <View
                            row
                            m="0.25rem 0 0 0">
                            <Button
                                size="sm"
                                m="0 auto 0 0"
                                onClick={() => setEdit(false)}>
                                Cancel
                            </Button>
                            <Button
                                size="xs"
                                style={{
                                    '--f-button-background-color': 'var(--f-color-surface-strong)',
                                    '--f-button-background-color-hover': 'var(--f-color-surface-stronger)',
                                }}
                                shadow="none"
                                border="none"
                                radius={50}
                                width={35}
                                height={35}
                                p={0}
                                onClick={() => {
                                    dispatchCustomEvent('tiptap:submit', { instanceId: id })
                                    setEdit(false)
                                }}>
                                <Icon
                                    icon={TbArrowUp}
                                    color="var(--f-color-accent)"
                                />
                            </Button>
                        </View>
                    </div>
                )}

                {!!attachments && (
                    <div className="f-message-bubble__attachments f-row">
                        {attachments.map((attachment, index) => (
                            <CommentAttachment
                                key={index}
                                attachment={attachment}
                                onDelete={() => handleAttachmentDelete(attachment)}
                            />
                        ))}
                    </div>
                )}

                {!!preview && (
                    <a
                        target="_blank"
                        href={preview.url}
                        className="f-message-bubble__preview f-buttonize-outline">
                        {!!preview.ogImage && !!preview.ogImage[0] && (
                            <Image
                                height={120}
                                src={preview.ogImage[0].url}
                                errorContent="Image not found"
                            />
                        )}
                        {!!preview.ogTitle && <h5 className="f-heading">{preview.ogTitle}</h5>}
                        {!!preview.ogDescription && <span className="f-text">{preview.ogDescription}</span>}
                    </a>
                )}

                <div className="f-message-bubble__header f-row">
                    <span className="f-message-bubble__header-name f-text sm">
                        {isMe ? 'You' : userName || externalEmail}
                    </span>

                    <span className="f-message-bubble__header-date f-text sm">{dateText}</span>
                </div>

                {!!Object.keys(reactions).length && (
                    <div className="f-message-bubble__user-reactions f-row">
                        {Object.keys(reactions).map((reaction, index) => (
                            <span
                                key={reaction}
                                onClick={() => handleReactionDelete(reactions[reaction].reactions)}
                                className="f-message-bubble__user-reaction f-buttonize f-row">
                                <Emoji
                                    unified={reaction}
                                    size={18}
                                />
                                &nbsp;
                                {reactions[reaction].count}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export const Comments = ({ externalEmail, taskId, projectId, teamId, workspaceId, isBelow = false }) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const taskCommentsAPI = useTaskComments()
    const [files, setFiles] = useState([])
    const [internal, setInternal] = useState(false)
    const { show, hide, visible } = useVisibility()
    const fileRef = useRef<any>(null)
    const { setDialog, closeDialog } = useDialog()
    const { upload } = useFile()
    const [html, setHtml] = useState('')
    const [json, setJson] = useState<any>({})
    const user = useAppStore((state) => state.user)
    const actions = useAppStore((state) => state.actions)
    const comments = useAppStore((state) => state.comments)
    const [edit, setEdit] = useState(null)
    const { removeAll } = useFile()
    const { getUserId } = useAuth()
    const instanceId = useId()

    const handleFileUpload = async (rawFiles) => {
        setLoading(true)
        setError(null)

        try {
            const fileArray: any = Array.from(rawFiles)

            const fileUploads: any = await Promise.all(
                fileArray.map((file: any) => {
                    return upload({
                        folder: workspaceId,
                        file,
                        secure: true,
                    })
                })
            )
            const files = fileUploads.map(({ url }, index) => {
                return {
                    name: fileArray[index].name,
                    size: fileArray[index].size,
                    url,
                    type: fileArray[index].type,
                }
            })

            setFiles(files)
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleFileChange = async (e) => {
        if (e.target.files.length != 0) handleFileUpload(e.target.files)
    }

    const handleFileClick = (e) => (fileRef.current ? fileRef.current.click() : null)

    const handleEdit = async (comment, json = { content: [] }, editor, id) => {
        if (isRichTextBlank(comment)) return

        // TODO: support mentions
        const mentions = getMentionsfromTipTapJSON(json)

        editor.commands.clearContent()
        editor.commands.focus()

        actions.commentUpdate({ id, comment })
        setEdit(false)
        await taskCommentsAPI.update(workspaceId, projectId, taskId, id, { comment })
    }

    const handleAddComment = async (comment, json = { content: [] }, editor) => {
        if (isRichTextBlank(comment)) return

        const commentId = uuidv4()
        const mentions = getMentionsfromTipTapJSON(json)

        const attachments = files.map((file) => ({
            id: uuidv4(),
            name: file.name,
            filesize: file.size,
            url: stripS3Url(file.url),
            type: file.type,
        }))

        editor.commands.clearContent()
        editor.commands.focus()

        actions.taskUpdate({
            task: {
                id: taskId,
                comments: comments.length + 1,
            },
        })

        actions.commentAdd({
            id: commentId,
            comment,
            attachments,
            userId: user.id,
            userName: user.name,
            userImage: user.image,
            createdAt: new Date().toISOString(),
        })

        setFiles([])

        // there's only 1 listener here
        dispatchCustomEvent('sidebararearoute:scroll-down')

        // there could be multiple (channel + thread)
        dispatchCustomEvent('scrollview:scroll-down', { instanceId })

        await taskCommentsAPI.create(workspaceId, projectId, taskId, {
            id: commentId,
            comment,
            attachments,
            mentions,
        })
    }

    const handleDelete = async (comment) => {
        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            actions.commentDelete(comment.id)
                            actions.taskUpdate({ task: { id: taskId, comments: comments.length - 1 } })
                            closeDialog()
                            taskCommentsAPI.remove(workspaceId, projectId, taskId, comment.id)
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const comments = await taskCommentsAPI.getAll(workspaceId, projectId, taskId)
            actions.setComments(comments)
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(e.message)
        }
    }

    const renderMessagesInner = () => {
        return (
            <div className="messages-inner">
                {comments.map((comment, index) => {
                    const previousMessage = index == 0 ? null : comments[index - 1]
                    const then = !previousMessage ? new Date('3 December 1999') : new Date(previousMessage.createdAt)
                    const now = new Date(comment.createdAt)
                    const isConsecutive =
                        now.getTime() - then.getTime() < CONSECUTIVE_TIME && previousMessage.userId == comment.userId

                    return (
                        <CommentBubble
                            key={index}
                            externalEmail={externalEmail}
                            isConsecutive={isConsecutive}
                            comment={comment}
                            onDelete={() => handleDelete(comment)}
                            onEdit={(c, j, e) => handleEdit(c, j, e, comment.id)}
                            onEditStart={() => setEdit(true)}
                            workspaceId={workspaceId}
                            projectId={projectId}
                            taskId={taskId}
                            teamId={teamId}
                        />
                    )
                })}

                {!comments.length && !loading && (
                    <View
                        column
                        position="absolute"
                        style={{ inset: 0 }}
                        gap="1rem">
                        <Icon
                            icon={TbMessageCircle}
                            color="var(--f-color-surface-strongest)"
                            style={{
                                '--f-icon-sizing-md': '7rem',
                                '--f-icon-stroke-width-md': '0.75',
                            }}
                        />
                        <Heading
                            as="h4"
                            textAlign="center">
                            No comments yet.
                        </Heading>
                        <Text textAlign="center">
                            Be the first to leave a comment on this task.
                        </Text>
                    </View>
                )}
            </div>
        )
    }

    useEffect(() => {
        if (taskId && projectId && workspaceId) fetchData()
    }, [taskId, projectId, workspaceId])

    return (
        <UploadArea
            className="task__comments-upload f-row"
            onDrop={handleFileUpload}>
            {(over) => (
                <div className="messages-container f-col">
                    {over && (
                        <div className="messages-droparea f-fadein f-col">
                            <Icon
                                icon={TbFileUpload}
                                color="var(--f-color-text-weak)"
                                style={{
                                    '--f-icon-sizing-md': '7rem',
                                    '--f-icon-stroke-width-md': '1',
                                }}
                            />

                            <Heading
                                as="h4"
                                fontWeight={600}>
                                Drop files here to upload
                            </Heading>
                        </div>
                    )}

                    <LoadingLine loading={loading} />

                    <div className="messages">
                        {!isBelow && (
                            <ScrollView
                                freeze={edit}
                                stickToBottom
                                instanceId={instanceId}
                                className="f-overflow-y-auto f-scrollbar messages-scroll">
                                {renderMessagesInner()}
                            </ScrollView>
                        )}

                        {isBelow && renderMessagesInner()}
                    </div>

                    <div className="messages__footer-container f-col">
                        <div className="messages__footer f-row">
                            <div className="messages__footer-input">
                                <Tiptap
                                    hideToolbar
                                    content=""
                                    onSubmit={handleAddComment}
                                    workspaceId={workspaceId}
                                    projectId={projectId}
                                    teamId={teamId}
                                    instanceId="comments"
                                />

                                {files.map(({ name }, index) => (
                                    <InputAttachment
                                        key={index}
                                        filename={name}
                                        onDelete={() => setFiles(files.filter((f, i) => i != index))}
                                    />
                                ))}

                                <div className="messages__footer-toolbar f-align-h-middle-left">
                                    <input
                                        multiple
                                        type="file"
                                        className="hide"
                                        ref={fileRef}
                                        onChange={handleFileChange}
                                    />

                                    <Button
                                        subtle
                                        size="xs"
                                        onClick={handleFileClick}>
                                        <IconLib icon="paperclip" />
                                    </Button>

                                    <Button
                                        subtle
                                        size="xs"
                                        onClick={() =>
                                            dispatchCustomEvent('tiptap:mention', { instanceId: 'comments' })
                                        }>
                                        <IconLib icon="at" />
                                    </Button>

                                    <Popover
                                        border="none"
                                        width="fit-content"
                                        portal={Portal}
                                        content={
                                            <EmojiPicker
                                                onEmojiClick={({ emoji }) => {
                                                    dispatchCustomEvent('tiptap:emoji', {
                                                        instanceId: 'comments',
                                                        emoji,
                                                    })
                                                    hide()
                                                }}
                                            />
                                        }
                                        isVisible={visible}
                                        onDismiss={hide}>
                                        <Button
                                            subtle
                                            size="xs"
                                            m="0 auto 0 0"
                                            onClick={show}
                                            className={visible ? undefined : 'reveal-on-hover-target'}>
                                            <Icon icon={TbMoodSmile} />
                                        </Button>
                                    </Popover>

                                    <Button
                                        size="xs"
                                        style={{
                                            '--f-button-background-color': 'var(--f-color-surface-strong)',
                                            '--f-button-background-color-hover': 'var(--f-color-surface-stronger)',
                                        }}
                                        shadow="none"
                                        border="none"
                                        radius={50}
                                        width={35}
                                        height={35}
                                        p={0}
                                        onClick={() =>
                                            dispatchCustomEvent('tiptap:submit', { instanceId: 'comments' })
                                        }>
                                        <Icon icon={TbArrowUp} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <View
                            row
                            colorToken="text-weaker"
                            justifyContent="flex-end"
                            width={30}
                            position="absolute"
                            className="f-buttonize"
                            zIndex={1}
                            style={{ bottom: 30, right: 30 }}
                        />
                    </div>
                </div>
            )}
        </UploadArea>
    )
}
